import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutData } from '@/mocks/about';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于狼友域名',
  description: '狼友域名是专为站长和资源爱好者打造的专业域名导航与资源聚合平台。了解我们的品牌故事、服务理念和联系方式。',
  url: 'https://langyouyuming.com/about',
};

const orgFullSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '狼友域名',
  url: 'https://langyouyuming.com/',
  description: '专业域名导航与资源聚合平台，为站长和资源爱好者提供最新可用域名地址、镜像备用链接及防失联入口服务。',
  email: 'contact@langyouyuming.com',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'contact@langyouyuming.com',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const faqFullSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: aboutData.faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function About() {
  const [formUrl, setFormUrl] = useState('https://readdy.ai/api/form/d9rgo36e3oq4jqumu160');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormSubmitted(true);
      return;
    }
    formData.delete('phone_alt');

    try {
      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch (e) { /* ignore */ }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;

      if (res.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else if (!res.ok || (serverMsg && serverMsg.includes && serverMsg.includes('spam'))) {
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      } else {
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgFullSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqFullSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-8">
              <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            </div>

            {/* Brand Intro */}
            <section className="mb-16">
              <div className="max-w-3xl">
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
                  关于狼友域名
                </h1>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  {aboutData.brand.intro}
                </p>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  {aboutData.brand.mission}
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  {aboutData.brand.vision}
                </p>
              </div>
            </section>

            {/* Stats */}
            <section className="mb-16">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {aboutData.stats.map((stat) => (
                  <div key={stat.label} className="bg-background-50 rounded-xl p-5 border border-background-200/70 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-xl">
                      <i className={`${stat.icon} text-accent-600 text-lg`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-foreground-950">{stat.value}</div>
                    <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Values */}
            <section className="mb-16">
              <h2 className="font-heading text-2xl text-foreground-950 mb-6">核心价值观</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {aboutData.brand.values.map((v) => (
                  <div key={v.title} className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                    <h3 className="font-heading text-lg text-foreground-950 mb-2">{v.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-16">
              <h2 className="font-heading text-2xl text-foreground-950 mb-2">用户口碑</h2>
              <p className="text-sm text-foreground-500 mb-6">听听用户们怎么说</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {aboutData.reputation.map((rep, idx) => (
                  <div key={idx} className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`text-sm ${i < rep.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{rep.content}</p>
                    <div>
                      <div className="text-sm font-medium text-foreground-950">{rep.name}</div>
                      <div className="text-xs text-foreground-400">{rep.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-16">
              <h2 className="font-heading text-2xl text-foreground-950 mb-6">常见问题</h2>
              <div className="space-y-3 max-w-3xl">
                {aboutData.faqs.map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden cursor-pointer">
                    <summary className="flex items-center justify-between p-5 font-medium text-foreground-950 text-sm list-none">
                      <span>{faq.question}</span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section>
              <h2 className="font-heading text-2xl text-foreground-950 mb-6">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Contact Info */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400 mb-0.5">电子邮箱</div>
                      <div className="text-sm text-foreground-950">{aboutData.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                      <i className="ri-wechat-line text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400 mb-0.5">微信公众号</div>
                      <div className="text-sm text-foreground-950">{aboutData.contact.wechat}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                      <i className="ri-telegram-line text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400 mb-0.5">Telegram</div>
                      <div className="text-sm text-foreground-950">{aboutData.contact.telegram}</div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-400 mt-2">{aboutData.contact.response}</p>
                </div>

                {/* Contact Form */}
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                  <h3 className="font-heading text-lg text-foreground-950 mb-4">发送消息</h3>
                  {formSubmitted ? (
                    <div className="text-center py-8">
                      <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-full">
                        <i className="ri-check-line text-2xl text-accent-600"></i>
                      </div>
                      <p className="text-foreground-950 font-medium">消息已发送！</p>
                      <p className="text-sm text-foreground-500 mt-1">我们会尽快回复您的咨询</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form>
                      {formError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                          {formError}
                        </div>
                      )}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                          <input
                            type="text"
                            name="name"
                            required
                            className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-300 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                          <input
                            type="email"
                            name="email"
                            required
                            className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-300 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                          <textarea
                            name="message"
                            required
                            maxLength={500}
                            rows={4}
                            className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                            placeholder="请输入您的消息（500字以内）"
                          ></textarea>
                          <div className="text-xs text-foreground-400 mt-1">最多500个字符</div>
                        </div>
                        <div className="honeypot-field">
                          <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                        </div>
                        <button
                          type="submit"
                          className="w-full py-3 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          发送消息
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}